import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FlowModal from "../components/FlowModal.jsx";
import ResultModal from "../components/ResultModal.jsx";
import Shimmer from "../components/Shimmer.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useChainTime } from "../hooks/useChainTime.js";
import { useDeposits, DEPOSIT_STATE } from "../hooks/useDeposits.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import { formatUsdcCompact } from "../utils/format.js";

/**
 * Abandoned-deposit recovery surface (spec §5 Phase 3).
 *
 * A deposit becomes Abandoned 366 days after maturity if the user never
 * settled, rolled over, or otherwise interacted with it. From that
 * point a 5%/month maintenance fee siphons the Safe Vault slice toward
 * the Marketing/Ops wallet; the position takes ~20 months to fully
 * deplete.
 *
 * The user can still withdraw the REMAINING balance at any time —
 * this page surfaces every Abandoned deposit they own, shows how much
 * is left, projects roughly how long until full depletion at the
 * current 5%/month rate, and offers a one-click `withdrawAbandoned`
 * call.
 *
 * Why a dedicated page (instead of just leaving the action inside
 * TdDetail): the abandoned flow is unusual enough that the user
 * probably hasn't been back to the position page in months. Linking
 * directly from the sidebar / Action Required card makes the recovery
 * discoverable without forcing the user to remember which deposit id
 * was at risk.
 */
export default function AbandonedRecovery() {
    const { address, signer } = useWallet();
    const { deposits, isLoading, refresh, patchDeposit } = useDeposits(address);

    // Lifecycle filter — Abandoned is state 4 per the on-chain enum and
    // matches DEPOSIT_STATE.Abandoned. We don't pull dormant/matured here
    // because those still have their full balance (no decay applied).
    const abandoned = useMemo(
        () =>
            deposits.filter(
                (d) => Number(d.state) === DEPOSIT_STATE.Abandoned
            ),
        [deposits]
    );

    // Sort by remaining balance descending — recovering the most valuable
    // position first is the practical priority. Ties broken by deposit id.
    const sorted = useMemo(
        () =>
            abandoned.slice().sort((a, b) => {
                const ba = BigInt(b.principal);
                const aa = BigInt(a.principal);
                if (ba > aa) return 1;
                if (ba < aa) return -1;
                return Number(b.id) - Number(a.id);
            }),
        [abandoned]
    );

    const [busyId, setBusyId] = useState(null);
    const [flowOpen, setFlowOpen] = useState(false);
    const [steps, setSteps] = useState([]);
    const [result, setResult] = useState(null);

    async function handleRecover(deposit) {
        if (!signer) return;
        setBusyId(deposit.id);
        setSteps([
            {
                key: "withdraw",
                label: `Withdrawing Term Deposit #${deposit.id}`,
                doneLabel: `Withdrawn Term Deposit #${deposit.id}`,
                status: "loading"
            }
        ]);
        setFlowOpen(true);
        setResult(null);
        try {
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await bond.withdrawAbandoned(deposit.id, overrides);
            await tx.wait();
            setSteps((prev) => prev.map((s) => ({ ...s, status: "done" })));
            // Optimistic flip so the row drops out of the list before the
            // next /deposits poll lands.
            patchDeposit(deposit.id, { state: DEPOSIT_STATE.Closed });
            setFlowOpen(false);
            setResult({
                type: "success",
                title: "Recovery Complete",
                message: `Term Deposit #${deposit.id} closed. The remaining balance of your Safe Vault slice has been returned to your wallet.`
            });
            refresh().catch(() => {});
        } catch (err) {
            setSteps((prev) => prev.map((s) => ({ ...s, status: "error" })));
            setFlowOpen(false);
            setResult({
                type: "error",
                title: "Recovery Failed",
                message: decodeContractError(err)
            });
        } finally {
            setBusyId(null);
        }
    }

    if (!address) {
        return <ConnectWalletPrompt />;
    }

    return (
        <div className="-mt-6 space-y-6">
            <header className="space-y-2">
                <h1 className="font-manrope text-[22px] font-bold text-[#b98c23] sm:text-[26px]">
                    Abandoned Deposit Recovery
                </h1>
                <p className="max-w-2xl text-[12.5px] leading-snug text-[#10281b]/80">
                    Per spec §5, deposits left untouched 366 days past maturity
                    enter the Abandoned phase and begin paying a 5% / month
                    maintenance fee on the Safe Vault slice. You can withdraw
                    whatever remains of your Safe Vault balance at any time.
                    Recovering sooner means more capital comes back to your wallet.
                </p>
            </header>

            <PolicyStrip />

            {isLoading ? (
                <LoadingState />
            ) : sorted.length === 0 ? (
                <EmptyState />
            ) : (
                <ul className="space-y-3">
                    {sorted.map((deposit) => (
                        <AbandonedRow
                            key={deposit.id}
                            deposit={deposit}
                            busy={busyId === deposit.id}
                            disabled={Boolean(busyId) && busyId !== deposit.id}
                            onRecover={() => handleRecover(deposit)}
                        />
                    ))}
                </ul>
            )}

            <FlowModal
                open={flowOpen}
                title={busyId ? `Recovering Deposit #${busyId}` : "Recovering Deposit"}
                steps={steps}
            />
            <ResultModal
                open={Boolean(result)}
                type={result?.type}
                title={result?.title}
                message={result?.message}
                primaryAction={
                    result?.type === "success"
                        ? { label: "Back to Term Deposits", to: "/term-deposits" }
                        : undefined
                }
                onClose={() => setResult(null)}
            />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

function PolicyStrip() {
    const items = [
        {
            label: "Decay rate",
            value: "5% / month",
            hint: "Of the Safe Vault slice"
        },
        {
            label: "Full depletion",
            value: "~20 months",
            hint: "From start of Abandoned phase"
        },
        {
            label: "Withdrawal",
            value: "Always open",
            hint: "Closes the position immediately"
        },
        {
            label: "Fees collected",
            value: "Marketing / Ops",
            hint: "Per spec §5 Phase 3"
        }
    ];
    return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item) => (
                <div
                    key={item.label}
                    className="rounded-[10px] border border-[#bfb67b]/40 bg-white px-4 py-3"
                >
                    <div className="text-[9.5px] font-bold uppercase tracking-widest text-[#10281b]/60">
                        {item.label}
                    </div>
                    <div className="mt-1 font-manrope text-[15px] font-bold text-[#10281b]">
                        {item.value}
                    </div>
                    <div className="text-[10.5px] text-[#10281b]/65">{item.hint}</div>
                </div>
            ))}
        </div>
    );
}

function AbandonedRow({ deposit, busy, disabled, onRecover }) {
    // Chain time so decay-month estimates match the local-node state
    // after a time warp.
    const { chainNowSec } = useChainTime();
    // `principal` on an Abandoned deposit is the ORIGINAL principal at
    // open time; the actual recoverable amount is the SafeVault slice
    // less any decay fees already collected. The contract holds the
    // authoritative number (`deposit.safeVaultAmount` minus already-
    // assessed fees), but we don't have that breakdown in the indexed
    // row — we surface the original principal as a ceiling and let the
    // success modal report the exact recovered amount once the tx lands.
    const principal = BigInt(deposit.principal || "0");
    const safeVaultSlice = (principal * BigInt(config.protocol.safeVaultBps)) / 10000n;

    // Days since maturity. Contracts measure decay in MONTHS so we round
    // down — the strip shows "approx" wording to head off precision
    // questions.
    const nowSec = chainNowSec;
    const maturitySec = Number(deposit.maturityTime || 0);
    const daysSinceMaturity = Math.max(0, Math.floor((nowSec - maturitySec) / 86400));
    const graceWindowDays = 365;
    const daysIntoDecay = Math.max(0, daysSinceMaturity - graceWindowDays);
    const monthsIntoDecay = Math.floor(daysIntoDecay / 30);

    // Estimated remaining = safeVault * 0.95^months. Cap months at 20 so
    // the floor sticks at ~36% (the spec's depletion timeline). Pure
    // estimate — the contract value is canonical, but this gives the
    // user enough signal to prioritise.
    const cappedMonths = Math.min(monthsIntoDecay, 20);
    const decayFactor = Math.pow(0.95, cappedMonths);
    const estimatedRemaining = BigInt(
        Math.floor(Number(safeVaultSlice) * decayFactor)
    );

    return (
        <li className="rounded-[12px] border border-[#bfb67b] bg-white px-4 py-4 shadow-sm sm:px-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <Link
                            to={`/term-deposits/${deposit.id}`}
                            className="font-manrope text-[14.5px] font-bold text-[#10281b] hover:underline"
                        >
                            Term Deposit #{String(deposit.id).padStart(6, "0")}
                        </Link>
                        <span className="inline-flex items-center rounded-full bg-[#C93030]/10 px-2 py-0.5 text-[10.5px] font-semibold text-[#C93030]">
                            Abandoned · {monthsIntoDecay} mo of decay
                        </span>
                    </div>
                    <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11.5px] sm:grid-cols-4">
                        <Stat label="Original principal" value={`$${formatUsdcCompact(principal)}`} />
                        <Stat label="Safe Vault slice" value={`$${formatUsdcCompact(safeVaultSlice)}`} />
                        <Stat label="Est. remaining" value={`~$${formatUsdcCompact(estimatedRemaining)}`} />
                        <Stat label="Days past maturity" value={`${daysSinceMaturity} d`} />
                    </dl>
                </div>
                <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
                    <button
                        type="button"
                        onClick={onRecover}
                        disabled={busy || disabled}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#10281b] px-5 py-1.5 font-manrope text-[12px] font-semibold text-[#e6c985] hover:-translate-y-0.5 hover:shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:translate-y-0"
                    >
                        {busy ? "Recovering…" : "Recover Now"}
                        <ChevronRight />
                    </button>
                    <Link
                        to={`/term-deposits/${deposit.id}`}
                        className="text-[10.5px] font-semibold text-[#10281b]/70 hover:text-[#10281b] hover:underline"
                    >
                        View detail
                    </Link>
                </div>
            </div>
        </li>
    );
}

function Stat({ label, value }) {
    return (
        <div className="min-w-0">
            <dt className="text-[9.5px] font-bold uppercase tracking-widest text-[#10281b]/55">
                {label}
            </dt>
            <dd className="mt-0.5 font-manrope text-[12.5px] font-semibold text-[#10281b]">
                {value}
            </dd>
        </div>
    );
}

function LoadingState() {
    return (
        <ul className="space-y-3">
            {[0, 1].map((i) => (
                <li
                    key={i}
                    className="rounded-[12px] border border-[#bfb67b]/40 bg-white px-5 py-5"
                >
                    <Shimmer width="12rem" height="1rem" />
                    <div className="mt-3 grid gap-2 sm:grid-cols-4">
                        <Shimmer width="8rem" height="0.875rem" />
                        <Shimmer width="8rem" height="0.875rem" />
                        <Shimmer width="8rem" height="0.875rem" />
                        <Shimmer width="8rem" height="0.875rem" />
                    </div>
                </li>
            ))}
        </ul>
    );
}

function EmptyState() {
    return (
        <div className="rounded-[12px] border border-[#bfb67b]/40 bg-white px-6 py-10 text-center">
            <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1D3E29]/10 text-[#1D3E29]">
                <ShieldIcon />
            </div>
            <h2 className="font-manrope text-[16px] font-bold text-[#10281b]">
                No abandoned deposits
            </h2>
            <p className="mt-1 text-[12px] text-[#10281b]/70">
                Nothing to recover right now. A deposit only enters this list
                if it sits 366 days past maturity without a settle or rollover.
            </p>
            <Link
                to="/term-deposits"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#10281b] px-4 py-1.5 text-[11.5px] font-semibold text-[#e6c985] hover:bg-[#0E2C19]"
            >
                Back to My Term Deposits
                <ChevronRight />
            </Link>
        </div>
    );
}

function ConnectWalletPrompt() {
    return (
        <div className="-mt-6 flex min-h-[60vh] items-center justify-center">
            <div className="max-w-md rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center">
                <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">
                    Wallet Required
                </h2>
                <p className="mt-2 text-[12px] text-[#e6c985]/80">
                    Connect your wallet to see if you have any abandoned
                    deposits to recover.
                </p>
            </div>
        </div>
    );
}

function ChevronRight() {
    return (
        <svg
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}

function ShieldIcon() {
    return (
        <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M12 3 L20 6 V12 C20 17 16 21 12 22 C8 21 4 17 4 12 V6 Z" />
            <polyline points="9 12 11 14 15 10" />
        </svg>
    );
}
